import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  HeartHandshake,
  Award,
  Users,
  Sparkles,
  Building2,
  MapPin,
  PhoneCall,
  ArrowRight,
  ChevronDown,
} from "lucide-react";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import "./About.css";

const SITE_NAME = "Elite Properties";
const CONTACT_PHONE = "+91 98765 43210";

// Optional: replace with your local images
const heroImg =
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80";
const p1 =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80";
const p2 =
  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80";
const p3 =
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80";

const STATS = [
  { n: "12+", t: "Years combined experience" },
  { n: "120+", t: "Curated projects" },
  { n: "₹1,400 Cr", t: "Property advised" },
  { n: "0%", t: "Broker fee to buyers" },
];

const VALUES = [
  {
    icon: ShieldCheck,
    t: "Verified only",
    d: "Every project is RERA-registered, title-checked and reference-verified before it reaches you.",
  },
  {
    icon: HeartHandshake,
    t: "Buyer-first",
    d: "Developers pay us, never you. Our advice stays honest because your outcome is the fee.",
  },
  {
    icon: Award,
    t: "Curated shortlist",
    d: "We reject nine projects for every one we list — location, build quality and exit potential.",
  },
  {
    icon: Users,
    t: "Senior advisors",
    d: "You speak to decision-makers who know each tower, not a rotating call-centre desk.",
  },
];

const STEPS = [
  {
    t: "Discovery",
    d: "A 30-minute call on budget, timeline, family needs and investment intent.",
  },
  {
    t: "Shortlist",
    d: "A curated set of 4–6 projects with pricing, carpet math and honest trade-offs.",
  },
  {
    t: "Site visits",
    d: "Chauffeured tours, same-day, with an advisor who answers the hard questions.",
  },
  {
    t: "Negotiation",
    d: "We benchmark against real closed deals and negotiate price, floor rise and payment plan.",
  },
  {
    t: "Handover",
    d: "Paperwork, home loan coordination, registration and possession support.",
  },
];

const TEAM = [
  {
    n: "Aarav Mehta",
    r: "Founder & Principal Advisor",
    d: "Ex-developer sales head. Fifteen years across Mumbai and Pune high-rises.",
  },
  {
    n: "Ishita Rao",
    r: "Head of Advisory",
    d: "Guides end-users through carpet, cost and community fit with rare patience.",
  },
  {
    n: "Kabir Nair",
    r: "Investments Lead",
    d: "Builds yield and exit models for investors buying pre-launch inventory.",
  },
];

const CITIES = [
  { name: "Pune", slug: "pune" },
  { name: "Mumbai", slug: "mumbai" },
  { name: "Gurgaon", slug: "gurgaon" },
  { name: "Bangalore", slug: "bangalore" },
  { name: "Hyderabad", slug: "hyderabad" },
  { name: "Noida", slug: "noida" },
];

const FAQS = [
  {
    q: "Do you charge buyers a fee?",
    a: "No. We are compensated by the developer after a successful transaction, at no extra cost to you — the price you pay is the price on the developer's rate sheet.",
  },
  {
    q: "How do you choose which projects to list?",
    a: "We score every project on developer track record, legal clarity, construction quality, location fundamentals and resale depth. Only the top decile makes the shortlist.",
  },
  {
    q: "Can you help with home loans?",
    a: "Yes. We coordinate with private and public lenders, compare sanctioned rates and manage documentation until disbursement.",
  },
  {
    q: "Do you work with NRI buyers?",
    a: "Frequently. We handle virtual walkthroughs, POA paperwork, repatriation guidance and remote registration coordination.",
  },
];

export default function About() {
  return (
    <div className="about-page">
      <Navbar />

      {/* Hero */}
      <section className="ab-hero">
        <div className="ab-hero-inner">
          <div>
            <span className="ab-eyebrow">
              <Sparkles className="h-3.5 w-3.5" /> Who We Are
            </span>
            <h1 className="ab-title">
              An advisory for people who want <em>more than a home.</em>
            </h1>
            <p className="ab-lede">
              {SITE_NAME} exists because premium real estate deserves a premium
              way to buy it. No brokerage games, no stale listings — only curated
              projects, transparent numbers, and senior advisors who pick up the
              phone.
            </p>
            <div className="ab-cta-row">
              <Link to="/contact" className="ab-btn-gold">
                Talk to an advisor <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/projects/residential" className="ab-btn-ghost">
                <Building2 className="h-4 w-4" /> Browse projects
              </Link>
            </div>
          </div>

          <div className="ab-hero-media">
            <img
              src={heroImg}
              alt="Premium residential tower at dusk"
              className="ab-hero-img"
              loading="lazy"
            />
            <div className="ab-hero-badge">
              <div className="ab-hero-badge-n">98%</div>
              <div className="ab-hero-badge-t">Client referral rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="ab-section">
        <div className="ab-stats">
          {STATS.map((s) => (
            <div key={s.t} className="ab-stat">
              <div className="ab-stat-n">{s.n}</div>
              <div className="ab-stat-t">{s.t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="ab-section-alt">
        <div className="ab-section ab-split">
          <div>
            <div className="ab-kicker">Our story</div>
            <h2 className="ab-h2">
              Built by people who sat on the other side of the table.
            </h2>
            <div className="ab-prose">
              <p>
                We spent years inside developer sales teams watching good
                families make expensive decisions on incomplete information —
                inflated carpet claims, hidden floor-rise charges, possession
                dates that moved twice a year.
              </p>
              <p>
                So we built the opposite. A small, senior advisory that studies a
                project the way an owner would: the soil report, the approval
                file, the last five resale transactions in the micro-market, and
                whether the clubhouse will still feel good in year eight.
              </p>
              <p>
                Today we advise end-users and investors across six metros, and
                almost every new client arrives through someone we have already
                helped.
              </p>
            </div>
            <blockquote className="ab-quote">
              “A home is the largest cheque most people ever write. It deserves
              better than a sales pitch.”
              <span>Aarav Mehta · Founder</span>
            </blockquote>
          </div>

          <div className="ab-collage">
            <img src={p1} alt="Curated residential project" loading="lazy" />
            <img src={p2} alt="Commercial tower interior" loading="lazy" />
            <img src={p3} alt="Landscaped township" loading="lazy" />
            <img src={heroImg} alt="City skyline at night" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="ab-section">
        <div className="ab-head">
          <div className="ab-kicker">What we stand for</div>
          <h2 className="ab-h2">Four commitments we never negotiate.</h2>
          <p className="ab-sub">
            These principles decide which projects we list, how we quote, and
            what we tell you when a deal is wrong for you.
          </p>
        </div>
        <div className="ab-grid-4">
          {VALUES.map(({ icon: Icon, t, d }) => (
            <article key={t} className="ab-card">
              <span className="ab-icon">
                <Icon className="h-6 w-6" />
              </span>
              <h3 className="ab-card-t">{t}</h3>
              <p className="ab-card-d">{d}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="ab-section-alt">
        <div className="ab-section">
          <div className="ab-head">
            <div className="ab-kicker">How we work</div>
            <h2 className="ab-h2">From first call to keys in hand.</h2>
            <p className="ab-sub">
              A five-stage process, typically six to ten weeks, with one advisor
              accountable throughout.
            </p>
          </div>
          <div className="ab-steps">
            {STEPS.map((s, i) => (
              <div key={s.t} className="ab-step">
                <span className="ab-step-n">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="ab-step-t">{s.t}</h3>
                <p className="ab-step-d">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="ab-section">
        <div className="ab-head">
          <div className="ab-kicker">The team</div>
          <h2 className="ab-h2">Small by design. Senior by default.</h2>
          <p className="ab-sub">
            No juniors handed your file. The person who advises you is the person
            who negotiates for you.
          </p>
        </div>
        <div className="ab-team">
          {TEAM.map((m) => (
            <article key={m.n} className="ab-member">
              <div className="ab-avatar">{m.n.charAt(0)}</div>
              <div className="ab-member-body">
                <h3 className="ab-member-n">{m.n}</h3>
                <div className="ab-member-r">{m.r}</div>
                <p className="ab-member-d">{m.d}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Cities */}
      <section className="ab-section-alt">
        <div className="ab-section">
          <div className="ab-head">
            <div className="ab-kicker">Where we operate</div>
            <h2 className="ab-h2">Six metros, deep local knowledge.</h2>
            <p className="ab-sub">
              We only advise in markets where we walk the sites ourselves.
            </p>
          </div>
          <div className="ab-chips">
            {CITIES.map((c) => (
              <Link key={c.slug} to={`/city/${c.slug}`} className="ab-chip">
                <MapPin className="h-4 w-4" /> {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ab-section">
        <div className="ab-head">
          <div className="ab-kicker">Good questions</div>
          <h2 className="ab-h2">Everything buyers ask us first.</h2>
        </div>
        <div className="ab-faq">
          {FAQS.map((f) => (
            <details key={f.q} className="ab-faq-item">
              <summary>
                {f.q} <ChevronDown className="h-4 w-4 ab-faq-chevron" />
              </summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="ab-section">
        <div className="ab-cta">
          <h2>Let’s find the one worth buying.</h2>
          <p>
            Tell us your budget, city and timeline. You will get a curated
            shortlist within 48 hours — no spam, no pressure.
          </p>
          <div className="ab-cta-actions">
            <Link to="/contact" className="ab-btn-gold">
              Book a consultation <ArrowRight className="h-4 w-4" />
            </Link>
            {/* <a
              href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`}
              className="ab-btn-ghost"
            >
              <PhoneCall className="h-4 w-4" /> {CONTACT_PHONE}
            </a> */}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}